import { useState, useRef, useEffect, useCallback, cloneElement, Fragment } from 'react'
import { isPhoneDevice } from './hooks/useIsPhone'
import { registerGrokAction } from '../utils/grokRegistry'   // grok-layout-20261020

/**
 * Workspace — a split-tree layout shell.
 *
 * The body is a tree. A node is either:
 *   • a split:  { dir: 'row'|'column', sizes: number[], children: Node[] }
 *   • a leaf:   { panel: string }   ← panel key into the `panels` map
 *
 * `sizes` are flex weights (not pixels), so cells stay proportional when the
 * window resizes and a drag just moves weight between two neighbours.
 *
 * Panels-as-nodes: App renders the actual elements (chart, tape, chat) and
 * passes them in via `panels={{ chart: <FootprintChart/>, ... }}`. The tree
 * only references panel keys + positions. This keeps the chart's ~30 props
 * local to App and means the chart element is created once and stays mounted —
 * resizing or flipping orientation only changes a flex-basis, never remounts.
 *
 * v1 scope: 3 tight borderless cells, draggable seams, orientation flip,
 * localStorage persistence. Delete/split/stack/right-click are later steps.
 */

// ─── Per-window layout ───
// Each open window claims a slot: window.name remembers it across reloads,
// a localStorage heartbeat marks which slots are live. Window 1 keeps the
// original key (existing layout untouched); window 2+ get their own.
const SLOT_REG = 'cheddar_ws_slots'
const WIN_SLOT = (() => {
  try {
    const m = /(?:^|;)cheddarWs=(\d+)/.exec(window.name || '')
    let slot = m ? +m[1] : 0
    if (!slot) {
      const reg = JSON.parse(localStorage.getItem(SLOT_REG) || '{}')
      const now = Date.now()
      slot = 1
      while (reg[slot] && now - reg[slot] < 6000) slot++
      window.name = `${window.name ? window.name + ';' : ''}cheddarWs=${slot}`
    }
    const edit = (fn) => {
      try {
        const reg = JSON.parse(localStorage.getItem(SLOT_REG) || '{}')
        fn(reg); localStorage.setItem(SLOT_REG, JSON.stringify(reg))
      } catch {}
    }
    const beat = () => edit(r => { r[slot] = Date.now() })
    beat(); setInterval(beat, 2000)
    window.addEventListener('pagehide', () => edit(r => { delete r[slot] }))
    return slot
  } catch { return 1 }
})()
const STORAGE_KEY = WIN_SLOT === 1 ? 'cheddar_workspace_v4' : `cheddar_workspace_v4_w${WIN_SLOT}`
const PORTRAIT_MAX = 1081   // px; at this width or below, root stacks vertically
                            // (covers a 1080px-wide portrait monitor)
const MIN_WEIGHT = 0.08    // a cell can't be dragged smaller than this fraction

// Human labels for the right-click panel picker. Order here is the menu order.
const PANEL_LABELS = {
  chart:   'Chart',
  chart2:  'Chart 2',
  tape:    'Tape',
  wl:      'Watchlist',
  wallets: 'Wallets',
  portfolio: 'Portfolio',   // portfolio-20261027
  journal: 'Journal',
  notes:   'Notes',
  cal:     'Calendar',
  opt:     'Options',   // schwab-20261103
  chat:    'Chat',
  scan:    'Scan',
  wstats:  'Tracker',
  station: 'Trade',
}

// Per-panel DEVICE scope — the ONE place that decides where a panel is
// allowed to live. 'both' (default, omitted), 'desktop', or 'phone'. Drives
// BOTH the picker (what you can add) and the load-time strip (what survives in
// a saved layout). Add a device-restricted panel by adding one line here.
//
// Scope is a property of the hardware, not the window: this used to be keyed
// on (max-width: 768px), so rotating a phone to landscape (~852px) put the
// phone-only Trade panel out of scope and it vanished off the layout. See
// hooks/useIsPhone.
const PANEL_SCOPE = {
  chart2:  'desktop',   // Chart 2 — desktop only
  station: 'phone',     // Trade — phone only
}

// ─── Phone cell-size adjust mode ───
// Travel below this at pointer-up is a TAP, not a swipe. Decided at UP (never
// at DOWN) so a drag can't be mistaken for a tap.
const TAP_SLOP_PX = 6
// Armed goes idle after this. Any swipe refreshes it; a tap exits immediately.
const ADJUST_IDLE_MS = 4000
// True if `key` is allowed on this device.
const panelInScope = (key, phone = isPhoneDevice()) => {
  const scope = PANEL_SCOPE[key] || 'both'
  return scope === 'both' || (scope === 'phone') === phone
}

// Default tree — the reference layout for a fresh user (or a storage
// reset): wallets rail on the left, chart center stage, tape over scan in
// a right-hand column. Everything else (wl/journal/chat) is a right-click
// away in the panel picker. Existing users' stored trees are untouched —
// this only applies when localStorage has no valid tree.
function defaultTree() {
  // portfolio-20261027: Portfolio is a tab next to Wallets on desktop; on a
  // phone (cells stack) it gets its own cell right after Wallets.
  const phone = isPhoneDevice()
  return {
    sizes: phone ? [1, 1, 4, 1.3] : [1, 4, 1.3],
    children: [
      ...(phone ? [{ panel: 'wallets' }, { panel: 'portfolio' }]
        : [{ panels: ['wallets', 'portfolio'], active: 'wallets' }]),
      { panel: 'chart' },
      {
        dir: 'column',
        sizes: [1, 1.2],
        children: [
          { panel: 'tape' },
          { panel: 'scan' },
        ],
      },
    ],
  }
}

// ── Leaf schema: { panels: [key,...], active: key } ──
// A cell holds 1+ panels as tabs; `active` is the visible one. Legacy
// trees stored { panel: key } — normalizeTree migrates them in place on
// load, so cheddar_workspace_v4 keeps working without a reset. A panel
// key lives in AT MOST one cell (panels are single React elements; two
// cells would mean two live instances).
function normalizeTree(node) {
  if (!node) return node
  if (node.panel != null && node.panels == null) {
    return { panels: [node.panel], active: node.panel }
  }
  if (node.panels != null) {
    const active = node.panels.includes(node.active) ? node.active : (node.panels[0] ?? null)
    return { panels: [...node.panels], active }
  }
  return { ...node, children: node.children.map(normalizeTree) }
}

function isLeaf(node) {
  return node.panels != null || node.panel != null
}

// Any panel key found in a subtree (used when splitting the root).
function firstPanel(node) {
  if (node.panels != null) return node.panels[0] ?? 'chart'
  if (node.panel != null) return node.panel
  for (const c of node.children) {
    const p = firstPanel(c)
    if (p) return p
  }
  return 'chart'
}

// Airtight viewport enforcement: walk a (normalized) tree and remove any panel
// not allowed on the current viewport (PANEL_SCOPE). Leaves get their out-of-
// scope keys filtered out (active reset if it was one of them); a leaf emptied
// this way is dropped from its parent along with its size weight, and a split
// left with a single child collapses into that child. Returns null if the whole
// subtree is emptied (caller falls back to default). Runs at load so a layout
// saved on desktop (with Chart 2) opens clean on phone, and vice-versa.
function stripOutOfScope(node, phone = isPhoneDevice()) {
  if (!node) return null
  if (isLeaf(node)) {
    const keep = (node.panels ?? []).filter(k => panelInScope(k, phone))
    if (keep.length === 0) return null
    const active = keep.includes(node.active) ? node.active : keep[0]
    return { panels: keep, active }
  }
  // Split node: recurse, dropping emptied children and their size weights.
  const kids = []
  const sizes = []
  node.children.forEach((child, i) => {
    const s = stripOutOfScope(child, phone)
    if (s) {
      kids.push(s)
      sizes.push(node.sizes?.[i] ?? 1)
    }
  })
  if (kids.length === 0) return null
  if (kids.length === 1) return kids[0]   // collapse single-child split
  return { ...node, children: kids, sizes }
}

// portfolio-20261027: one-time add of the Portfolio panel to an EXISTING saved
// layout — desktop: a tab beside Wallets; phone: its own cell right after the
// Wallets cell. Runs once (flag), so closing it later sticks. No Wallets cell
// → nothing added (it's in the right-click picker).
const PORTFOLIO_SEED_KEY = 'cheddar_portfolio_seeded'
function seedPortfolio(tree, phone = isPhoneDevice()) {
  try {
    if (!tree || localStorage.getItem(PORTFOLIO_SEED_KEY)) return tree
    localStorage.setItem(PORTFOLIO_SEED_KEY, '1')
    const has = (n) => (n.panels ? n.panels.includes('portfolio') : (n.children || []).some(has))
    if (has(tree)) return tree
    const walk = (n) => {
      if (!n.children) return false
      for (let i = 0; i < n.children.length; i++) {
        const c = n.children[i]
        if (c.panels && c.panels.includes('wallets')) {
          if (phone) {
            n.children.splice(i + 1, 0, { panels: ['portfolio'], active: 'portfolio' })
            if (Array.isArray(n.sizes)) n.sizes.splice(i + 1, 0, n.sizes[i] ?? 1)
          } else c.panels.splice(c.panels.indexOf('wallets') + 1, 0, 'portfolio')
          return true
        }
        if (walk(c)) return true
      }
      return false
    }
    if (tree.panels && tree.panels.includes('wallets')) tree.panels.push('portfolio')
    else walk(tree)
  } catch {}
  return tree
}

function loadTree() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const t = JSON.parse(raw)
      if (t && Array.isArray(t.children) && t.children.length) {
        const stripped = seedPortfolio(stripOutOfScope(normalizeTree(t)))
        if (stripped && (stripped.children?.length || stripped.panels?.length)) return stripped
      }
    }
  } catch {}
  return normalizeTree(defaultTree())
}

// Header-rail surfaces — shared by the right-click picker and the
// double-click maximize. Rail CONTROLS (buttons/inputs/selects) are
// excluded at the call sites so refresh/tabs/clear never trigger either.
const RAIL_SELECTOR =
  '.panel-rail, .tape-panel-header, .wl-panel-header, .wallets-panel-header, .messages-panel-header, .journal-panel-header, .notes-panel-header'

export default function Workspace({ panels }) {
  const rootRef = useRef(null)
  // TRUE-PHONE flag — must match the stylesheet's phone rules exactly, and now
  // does so by construction: both ask hooks/useIsPhone, and the CSS gets its
  // answer from the html.is-phone class that same module sets. Content-driven
  // cell flow keys on THIS, not on isPortrait: isPortrait fires below 1081, but
  // the CSS releases that make content flow coherent only exist on a phone —
  // keying flow on isPortrait left 769-1080 (narrow desktop windows, tablet
  // portrait) half-converted, with seams adrift and drag dead.
  //
  // Not state, and no matchMedia listener: a phone doesn't stop being a phone
  // when you rotate it. That listener was the bug — landscape (~852px) crossed
  // the old 768 line and tore the layout apart mid-session.
  const isPhoneUI = isPhoneDevice()
  // ── PHONE: FIXED-BOX MODEL ──────────────────────────────────────
  // Every phone cell is a fixed-height box that scrolls INTERNALLY, and the
  // column of boxes scrolls as a page. That one sentence gives all three
  // things the content-flow model could never do together:
  //   • rails can't scroll away  — they're pinned inside a box that doesn't move
  //   • cells have initial sizes — the box has a default height (--phone-cell-h)
  //   • bottoms are adjustable   — a seam drags that height, directly
  // Content-driven flow (cells sizing to their content) was the opposite model,
  // and every fix bolted onto it broke a neighbour: content taller than a cell
  // pushed the rails up, "initial size" was meaningless because content decided,
  // and resize had no number to drag. One model now, one meaning per seam.
  // Heights are per-cell (keyed by tree path), persisted, all defaulting to the
  // SAME value so the workspace reads consistently.
  const [phonePx, setPhonePx] = useState(() => {
    try { return JSON.parse(localStorage.getItem('cheddar_phone_cell_px') || '{}') || {} } catch { return {} }
  })
  const onCellResize = useCallback((key, px) => {
    setPhonePx(prev => {
      const next = { ...prev }
      if (px == null) delete next[key]                      // back to the default height
      else next[key] = Math.round(Math.min(Math.max(px, 140), window.innerHeight * 2))
      try { localStorage.setItem('cheddar_phone_cell_px', JSON.stringify(next)) } catch {}
      return next
    })
  }, [])

  // (The app-level back-to-top arrow lived here. Removed: it was fixed to the
  // viewport and scrolled the whole workspace column, so it had to GUESS which
  // "top" you meant — it fired on any descendant scroller hitting its bottom,
  // then scrolled the page instead of the thing you'd been reading. Scroll-to-top
  // is now per-panel: see BackToTop.jsx, wired into ScanPanel and JournalPanel.
  // A panel doesn't have to guess. Its top is its own top.
  //
  // Its `animation: ws-top-fade` never did anything either — those keyframes were
  // never defined anywhere in the codebase. BackToTop defines its own.)

  // Orientation derived live from viewport width. Portrait (narrow) → stack
  // cells vertically (each a full-width horizontal strip). Landscape (wide) →
  // cells side by side. This is NOT persisted — it always reflects the screen.
  const [isPortrait, setIsPortrait] = useState(
    typeof window !== 'undefined' ? window.innerWidth < PORTRAIT_MAX : false
  )
  const [tree, setTree] = useState(() => loadTree())

  // Watch viewport width. matchMedia is the reliable signal for an
  // orientation/width breakpoint (ResizeObserver on the root can lag or
  // measure 0 during mount).
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia(`(max-width: ${PORTRAIT_MAX - 1}px)`)
    const apply = () => setIsPortrait(mq.matches)
    apply()
    // addEventListener('change') is the modern API; addListener is the fallback.
    if (mq.addEventListener) mq.addEventListener('change', apply)
    else mq.addListener(apply)
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply)
      else mq.removeListener(apply)
    }
  }, [])

  // Persist only structure + sizes; orientation is applied at render.
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(tree)) } catch {}
  }, [tree])

  // Adjust two adjacent weights at a tree path during a seam drag.
  const resizeAt = useCallback((path, index, deltaFrac) => {
    setTree(prev => {
      const next = structuredClone(prev)
      // Walk to the split node that owns this seam.
      let node = next
      for (const i of path) node = node.children[i]
      const sizes = node.sizes
      const total = sizes.reduce((a, b) => a + b, 0)
      const a = sizes[index], b = sizes[index + 1]
      const pair = a + b
      const deltaW = deltaFrac * total
      let na = a + deltaW
      let nb = b - deltaW
      const minW = MIN_WEIGHT * total
      if (na < minW) { na = minW; nb = pair - minW }
      if (nb < minW) { nb = minW; na = pair - minW }
      sizes[index] = na
      sizes[index + 1] = nb
      return next
    })
  }, [])

  // ─── Right-click panel picker ───
  // menu = { path, x, y } for the cell whose menu is open, or null.
  const [menu, setMenu] = useState(null)
  // Maximize: stores the maximized LEAF's path. Pure VIEW STATE — the
  // tree and cheddar_workspace_v4 never change; cells off the path go
  // display:none (hidden, NOT unmounted — every panel keeps its state,
  // sockets, scroll), on-path cells expand to 100%. No second render of
  // the panel element (two live instances would mean two canvases/bots).
  // Session-only by design. Esc or rail double-click restores.
  const [maximized, setMaximized] = useState(null)   // path array | null
  // Maximized cell also takes the APP header's space: the header slides up
  // out of view (CSS on html.ws-max), the footer stays. Its height is
  // measured at the moment of maximizing, since the header can wrap.
  useEffect(() => {
    const root = document.documentElement
    if (maximized) {
      const h = document.querySelector('header.header')?.offsetHeight || 0
      root.style.setProperty('--hdr-h', `${h}px`)
      root.classList.add('ws-max')
    } else {
      root.classList.remove('ws-max')
    }
    return () => root.classList.remove('ws-max')
  }, [maximized])

  useEffect(() => {
    if (!maximized) return
    const onKey = (e) => { if (e.key === 'Escape') setMaximized(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [maximized])

  // ─── Phone: cell-size ADJUST MODE ──────────────────────────────────────
  // Double-tapping a seam arms it. "Armed" used to mean *this 28px strip is
  // live* — your finger had to stay on the seam to resize. It now means *the
  // whole workspace is live*: a veil sits below the seam in z-order and above
  // everything else, so a swipe ANYWHERE drives the armed cell's height and a
  // tap (no travel) exits the mode. A swipe deliberately does NOT exit — you
  // stay armed and can keep nudging until you tap out.
  //
  // WHY armed lives here and not inside FlowSeam: exactly one seam may be
  // armed at a time. With per-seam local state, a second seam still pokes
  // through the first one's veil (seams z:20, veil z:19) — double-tap it and
  // you'd have two armed seams rendering two veils, fighting over the gesture.
  // One identity at the top makes that unrepresentable.
  //
  // Shape: { key, rect } — rect is the workspace box, measured ONCE at arm so
  // the veil covers the column and leaves the header/footer tappable. Re-arming
  // with the SAME key returns the identical object, so React bails out of the
  // render: no re-measure, no reflow. That matters because onArm() is called on
  // every pointermove of a drag to refresh the idle timer — it's on the hot path.
  const [armed, setArmed] = useState(null)
  const disarmRef = useRef(null)
  const onArm = useCallback((key) => {
    clearTimeout(disarmRef.current)
    if (key == null) { setArmed(null); return }
    setArmed(prev => (prev?.key === key
      ? prev                                             // same seam → bail, no layout read
      : { key, rect: rootRef.current?.getBoundingClientRect() }))
    disarmRef.current = setTimeout(() => setArmed(null), ADJUST_IDLE_MS)
  }, [])
  useEffect(() => () => clearTimeout(disarmRef.current), [])
  // Never strand the veil: leaving phone UI or maximizing a cell both remove
  // the seams it belongs to, and a veil with no seam under it is a dead screen.
  useEffect(() => {
    if (!isPhoneUI || maximized) { clearTimeout(disarmRef.current); setArmed(null) }
  }, [isPhoneUI, maximized])

  const openMenu = useCallback((path, x, y) => setMenu({ path, x, y }), [])
  const closeMenu = useCallback(() => setMenu(null), [])

  // Helpers to read/walk the tree by path.
  const nodeAt = (root, path) => {
    let n = root
    for (const i of path) n = n.children[i]
    return n
  }

  // Find the path of the leaf currently showing `panelKey` (or null).
  const findPanelPath = (node, key, path = []) => {
    if (isLeaf(node)) return node.panels?.includes(key) ? path : null
    for (let i = 0; i < node.children.length; i++) {
      const r = findPanelPath(node.children[i], key, [...path, i])
      if (r) return r
    }
    return null
  }

  // ─── Grok layout actions (grok-layout-20261020) ───
  // get_layout / adjust_layout / reset_layout, applied live through setTree.
  // Only the workspace tree (this window's STORAGE_KEY), phone cell heights
  // and the session maximize are touched — never wallets/keys/settings/
  // alerts/drawings/chat.
  const treeRef = useRef(tree)
  treeRef.current = tree
  const maxRef = useRef(maximized)
  maxRef.current = maximized
  useEffect(() => {
    const phone = isPhoneDevice()
    const KEYS = Object.keys(PANEL_LABELS).filter(k => panelInScope(k, phone))
    const panelKey = (v) => {
      const s = String(v || '').trim().toLowerCase()
      if (!s) return null
      if (KEYS.includes(s)) return s
      return KEYS.find(k => PANEL_LABELS[k].toLowerCase() === s) || null
    }
    const describe = (n) => {
      if (isLeaf(n)) return n.panels.length ? `[${n.panels.map(k => (k === n.active ? `*${k}` : k)).join(',')}]` : '[empty]'
      const tot = n.sizes.reduce((a, b) => a + b, 0) || 1
      return `${n.dir === 'column' ? 'col' : 'row'}(${n.children.map((c, i) => `${Math.round(100 * (n.sizes[i] ?? 1) / tot)}%:${describe(c)}`).join(' ')})`
    }
    const leaves = (n, p = [], out = []) => {
      if (isLeaf(n)) out.push(p)
      else n.children.forEach((c, i) => leaves(c, [...p, i], out))
      return out
    }
    const removeCellAt = (next, path) => {
      // same rules as closeCell; returns the new root
      if (path.length === 0) return next
      const parentPath = path.slice(0, -1), idx = path[path.length - 1]
      const parent = nodeAt(next, parentPath)
      if (parent.children.length <= 1) return next
      parent.sizes.splice(idx, 1); parent.children.splice(idx, 1)
      if (parent.children.length === 1) {
        if (parentPath.length === 0) return parent.children[0].panels ? { sizes: [1], children: [parent.children[0]] } : parent.children[0]
        const gp = nodeAt(next, parentPath.slice(0, -1))
        gp.children[parentPath[parentPath.length - 1]] = parent.children[0]
      }
      return next
    }
    const exitFocus = () => {
      setMaximized(null)
      try { document.documentElement.classList.remove('chart-focus') } catch {}
      try { if (document.fullscreenElement) document.exitFullscreen?.() } catch {}
    }
    const offGet = registerGrokAction('get_layout', {
      description: 'Read the current app panel layout (cells, sizes, open/missing panels, maximized/fullscreen). Use before adjusting or when the user says the layout looks wrong.',
      params: { type: 'object', properties: {} },
      handler: async () => {
        const t = treeRef.current
        const open = KEYS.filter(k => findPanelPath(t, k))
        return {
          ok: true,
          layout: describe(t),
          open, closed: KEYS.filter(k => !open.includes(k)),
          labels: Object.fromEntries(KEYS.map(k => [k, PANEL_LABELS[k]])),
          maximized: maxRef.current ? (nodeAt(t, maxRef.current)?.active || true) : false,
          fullscreen: !!document.fullscreenElement,
          phone, portrait: window.innerWidth < PORTRAIT_MAX,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          window: WIN_SLOT,
        }
      },
    })
    const offAdj = registerGrokAction('adjust_layout', {
      description: 'Bounded layout change. op: open (show panel; adds it as a tab next to `target` panel or in the last cell), close (remove panel; its cell closes if empty), resize (panel cell to `size` = 0.08-0.9 of its row/column), move (put panel as a tab in `target` panel\'s cell), exit_focus (leave maximized cell / fullscreen).',
      params: {
        type: 'object',
        properties: {
          op: { type: 'string', enum: ['open', 'close', 'resize', 'move', 'exit_focus'] },
          panel: { type: 'string', description: 'panel key or label, e.g. chart, tape, scan, wallets, chat' },
          target: { type: 'string', description: 'panel whose cell to use (open/move)' },
          size: { type: 'number', description: 'resize fraction 0.08-0.9' },
        },
        required: ['op'],
      },
      handler: async (a) => {
        const op = String(a.op || '')
        if (op === 'exit_focus') { exitFocus(); return { ok: true, message: 'Exited maximized/fullscreen view' } }
        const key = panelKey(a.panel)
        if (!key) return { ok: false, error: `unknown panel; use one of ${KEYS.join(', ')}` }
        const t = structuredClone(treeRef.current)
        const at = findPanelPath(t, key)
        let next = t
        if (op === 'open') {
          if (at) { nodeAt(t, at).active = key }
          else {
            const tp = a.target ? findPanelPath(t, panelKey(a.target)) : null
            const ls = leaves(t)
            const dest = nodeAt(t, tp || ls[ls.length - 1])
            dest.panels = [...dest.panels, key]; dest.active = key
          }
        } else if (op === 'close') {
          if (!at) return { ok: false, error: `${key} is not open` }
          const leaf = nodeAt(t, at)
          if (leaf.panels.length > 1) {
            leaf.panels = leaf.panels.filter(k => k !== key)
            if (leaf.active === key) leaf.active = leaf.panels[0]
          } else {
            if (leaves(t).length <= 1) return { ok: false, error: 'cannot close the only cell' }
            next = removeCellAt(t, at)
          }
        } else if (op === 'move') {
          const tk = panelKey(a.target)
          const tp = tk ? findPanelPath(t, tk) : null
          if (!at || !tp) return { ok: false, error: 'panel and target must both be open' }
          if (at.join() === tp.join()) { nodeAt(t, at).active = key }
          else {
            const src = nodeAt(t, at)
            if (src.panels.length > 1) {
              src.panels = src.panels.filter(k => k !== key)
              if (src.active === key) src.active = src.panels[0]
              const dest = nodeAt(t, tp); dest.panels = [...dest.panels, key]; dest.active = key
            } else {
              const dest = nodeAt(t, tp); dest.panels = [...dest.panels, key]; dest.active = key
              next = removeCellAt(t, at)
            }
          }
        } else if (op === 'resize') {
          if (!at || !at.length) return { ok: false, error: `${key} is not open in a resizable cell` }
          const f = Math.min(0.9, Math.max(MIN_WEIGHT, Number(a.size)))
          if (!Number.isFinite(f)) return { ok: false, error: 'size must be a number 0.08-0.9' }
          const parent = nodeAt(t, at.slice(0, -1)), idx = at[at.length - 1]
          const n = parent.children.length
          if (n < 2) return { ok: false, error: 'cell has no neighbours to resize against' }
          const tot = parent.sizes.reduce((x, y) => x + y, 0) || n
          const rest = tot - parent.sizes[idx]
          const restNew = tot * (1 - f)
          parent.sizes = parent.sizes.map((s, i) => (i === idx ? tot * f : Math.max(MIN_WEIGHT * tot, rest > 0 ? s * restNew / rest : restNew / (n - 1))))
        } else return { ok: false, error: 'op must be open, close, resize, move or exit_focus' }
        if (maxRef.current) setMaximized(null)
        setTree(next)
        return { ok: true, message: `${op} ${key} done`, layout: describe(next) }
      },
    })
    const offReset = registerGrokAction('reset_layout', {
      description: 'Restore the default panel layout for this window (wallets | chart | tape over scan) and default phone cell heights. Only layout; nothing else is cleared. Use when the layout is stuck/broken.',
      params: { type: 'object', properties: {} },
      handler: async () => {
        exitFocus()
        try { localStorage.removeItem(STORAGE_KEY); localStorage.removeItem('cheddar_phone_cell_px') } catch {}
        setPhonePx({})
        const t = stripOutOfScope(normalizeTree(defaultTree())) || normalizeTree(defaultTree())
        setTree(t)
        return { ok: true, message: 'Layout reset to default', layout: describe(t) }
      },
    })
    // opt-open-20261103: open/focus a panel from code — window event
    // 'cheddar-open-panel' {detail:{panel}}. Focuses it if open, otherwise adds
    // it as a tab in the last cell (same as adjust_layout op=open).
    const onOpenPanel = (ev) => {
      const key = panelKey(ev?.detail?.panel); if (!key) return
      const t = structuredClone(treeRef.current)
      const at = findPanelPath(t, key)
      if (at) nodeAt(t, at).active = key
      else { const ls = leaves(t); const dest = nodeAt(t, ls[ls.length - 1]); dest.panels = [...dest.panels, key]; dest.active = key }
      if (maxRef.current) setMaximized(null)
      setTree(t)
    }
    window.addEventListener('cheddar-open-panel', onOpenPanel)
    return () => { offGet(); offAdj(); offReset(); window.removeEventListener('cheddar-open-panel', onOpenPanel) }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle a panel's membership in a cell (checkbox semantics). Checked =
  // tab in this cell. Unchecking removes it; unchecking the LAST panel is
  // a no-op (use Close cell). Checking a panel that lives in another cell
  // MOVES it here — one instance rule. The menu stays open so several
  // panels can be added/removed in one visit; click-away dismisses.
  const togglePanel = useCallback((path, key) => {
    setTree(prev => {
      const next = structuredClone(prev)
      const target = nodeAt(next, path)
      if (!isLeaf(target)) return prev
      if (target.panels.includes(key)) {
        if (target.panels.length <= 1) return prev   // last panel: no-op
        target.panels = target.panels.filter(k => k !== key)
        if (target.active === key) target.active = target.panels[0]
        return next
      }
      // Remove from wherever it currently lives (move semantics).
      const existingPath = findPanelPath(next, key)
      if (existingPath) {
        const other = nodeAt(next, existingPath)
        other.panels = other.panels.filter(k => k !== key)
        if (other.active === key) other.active = other.panels[0] ?? null
      }
      target.panels = [...target.panels, key]
      target.active = key
      return next
    })
  }, [])

  // Switch the visible tab in a cell (persisted with the tree).
  const setActiveTab = useCallback((path, key) => {
    setTree(prev => {
      const next = structuredClone(prev)
      const leaf = nodeAt(next, path)
      if (!isLeaf(leaf) || !leaf.panels.includes(key)) return prev
      leaf.active = key
      return next
    })
  }, [])

  // paneldrag-20261028: drag one panel's tab onto another's → they swap places
  // (cell sizes stay put). Same cell → swaps tab order. Saved with the tree.
  const swapPanels = useCallback((a, b) => {
    if (!a || !b || a === b) return
    setTree(prev => {
      const next = structuredClone(prev)
      const found = []
      const walk = (n) => {
        if (isLeaf(n)) { if (n.panels && (n.panels.includes(a) || n.panels.includes(b))) found.push(n); return }
        ;(n.children || []).forEach(walk)
      }
      walk(next)
      if (!found.length) return prev
      for (const leaf of found) {
        leaf.panels = leaf.panels.map(k => (k === a ? b : k === b ? a : k))
        if (leaf.active === a) leaf.active = b
        else if (leaf.active === b) leaf.active = a
      }
      return next
    })
  }, [])

  // Split a cell into two along `dir`; the new sibling gets the same panel
  // (duplicate view) — user can immediately reassign it via the picker.
  const splitCell = useCallback((path, dir) => {
    setTree(prev => {
      const next = structuredClone(prev)
      if (path.length === 0) {
        // Splitting the root: wrap the whole tree.
        // New sibling is EMPTY (placeholder) — the old { panel: copy }
        // rendered the same panel twice = two live instances.
        return { dir, sizes: [1, 1], children: [next, { panels: [], active: null }] }
      }
      const parentPath = path.slice(0, -1)
      const idx = path[path.length - 1]
      const parent = nodeAt(next, parentPath)
      const leaf = parent.children[idx]
      // Replace the leaf with a split containing the leaf + a copy.
      parent.children[idx] = {
        dir,
        sizes: [1, 1],
        children: [leaf, { panels: [], active: null }],
      }
      return next
    })
    closeMenu()
  }, [closeMenu])

  // Close a cell: remove the leaf, give its weight back to siblings. If a split
  // drops to one child, collapse it into that child.
  const closeCell = useCallback((path) => {
    setTree(prev => {
      if (path.length === 0) return prev   // can't close the root
      const next = structuredClone(prev)
      const parentPath = path.slice(0, -1)
      const idx = path[path.length - 1]
      const parent = nodeAt(next, parentPath)
      if (parent.children.length <= 1) return prev
      parent.sizes.splice(idx, 1)
      parent.children.splice(idx, 1)
      // Collapse a now-single-child split into its child.
      if (parent.children.length === 1 && parentPath.length > 0) {
        const gpPath = parentPath.slice(0, -1)
        const pIdx = parentPath[parentPath.length - 1]
        const gp = nodeAt(next, gpPath)
        gp.children[pIdx] = parent.children[0]
      }
      return next
    })
    closeMenu()
  }, [closeMenu])

  return (
    <div className={`workspace-root${maximized ? ' ws-maximized' : ''}`} ref={rootRef}
    >
      <SplitNode
        node={tree}
        path={[]}
        panels={panels}
        onResize={resizeAt}
        onOpenMenu={openMenu}
        onMaximize={(p) => setMaximized(m => (m ? null : p))}
        onSetActive={setActiveTab}
        onSwap={swapPanels}
       
        maximized={maximized}
        flipDirs={isPortrait}
        contentFlow={isPhoneUI}
        phonePx={phonePx}
        onCellResize={onCellResize}
        armedKey={armed?.key ?? null}
        onArm={onArm}
      />
      {isPhoneUI && armed && (
        /* The adjust veil. Transparent — the amber seam shows straight through
           it; this catches touches, it doesn't paint.
             z-index 19: UNDER the seams (20), OVER everything in a cell (chart
           overlays 2–6, controls 10). So the armed seam keeps its own gestures
           (drag from the strip, double-tap to reset the cell to default) while
           every other touch in the workspace lands here.
             position: fixed inside .workspace-root is safe — nothing in the
           ancestor chain (.app / .workspace-root / .ws-split) sets a transform
           or z-index, so no stacking context intercepts it and the seams' z:20
           and this z:19 compete in the same (root) context. The back-to-top
           button two blocks up already relies on exactly this.
             touch-action: none is the whole point — without it the scrolling
           workspace treats a vertical drag as a pan and eats the resize. */
        <div
          style={{
            position: 'fixed',
            left: armed.rect?.left ?? 0,
            top: armed.rect?.top ?? 0,
            width: armed.rect?.width ?? '100%',
            height: armed.rect?.height ?? '100%',
            zIndex: 19,
            background: 'transparent',
            touchAction: 'none',
            cursor: 'row-resize',
          }}
          onPointerDown={(e) => {
            e.preventDefault()
            const key = armed.key
            // Scoped query, not document-wide: cellKey is a tree path, unique
            // by construction, but scoping costs nothing and can't surprise us.
            const cell = rootRef.current?.querySelector(`[data-ws-cell="${key}"]`)
            // Same start-height rule the seam uses: the stored px if this cell
            // has been dragged before, otherwise MEASURE the box — so the first
            // swipe continues from the height you're actually looking at.
            const px0 = phonePx?.[key] ?? (cell?.getBoundingClientRect().height || 300)
            const d = { y0: e.clientY, px0, moved: false }
            try { e.currentTarget.setPointerCapture(e.pointerId) } catch {}
            const move = (ev) => {
              const dy = ev.clientY - d.y0
              if (!d.moved && Math.abs(dy) > TAP_SLOP_PX) d.moved = true
              if (!d.moved) return
              onCellResize(key, d.px0 + dy)   // clamped inside onCellResize
              onArm(key)                      // swipe keeps the mode alive
            }
            const up = () => {
              window.removeEventListener('pointermove', move)
              window.removeEventListener('pointerup', up)
              window.removeEventListener('pointercancel', up)
              // TAP (no travel) = the exit gesture. A swipe leaves you armed.
              if (!d.moved) onArm(null)
            }
            window.addEventListener('pointermove', move)
            window.addEventListener('pointerup', up)
            window.addEventListener('pointercancel', up)
          }}
        />
      )}
      {menu && (
        /* "Split horizontal" = a horizontal SEAM (cells stack top/bottom =
           dir column); "vertical" = vertical seam (side-by-side = dir
           row). The label names the divider the user sees, not the flex
           direction — they're opposites, which bit once already. Portrait
           additionally renders every stored dir flipped, so each button
           stores the opposite of its landscape value there. */
        <CellMenu
          x={menu.x}
          y={menu.y}
          currentPanels={(() => { const n = nodeAt(tree, menu.path); return n?.panels ?? (n?.panel ? [n.panel] : []) })()}
          onToggle={(key) => togglePanel(menu.path, key)}
          onSplitH={() => splitCell(menu.path, isPortrait ? 'row' : 'column')}
          onSplitV={() => splitCell(menu.path, isPortrait ? 'column' : 'row')}
          onClose={() => closeCell(menu.path)}
          onDismiss={closeMenu}
        />
      )}
    </div>
  )
}

// tapdrag-20261028: touch has no HTML5 drag. Double-tap a tab to ARM it, then
// drag (same finger or the next touch from that tab) onto another tab → swap.
// Disarms on drop, on any touch elsewhere, or after TD_ARM_MS. Module-level +
// direct classList so it never re-renders the workspace.
const TD = { armed: null, armedEl: null, timer: null, last: null, drag: null, onSwap: null }
const TD_DOUBLE_MS = 350, TD_ARM_MS = 4000, TD_SLOP = 8
function tdOutside(e) { if (!TD.armedEl?.contains(e.target)) tdDisarm() }
function tdDisarm() {
  clearTimeout(TD.timer)
  TD.drag?.over?.classList.remove('ws-tab-drop')
  TD.drag?.ghost?.remove()
  TD.armedEl?.classList.remove('ws-tab-armed')
  TD.armed = null; TD.armedEl = null; TD.drag = null
  document.removeEventListener('touchstart', tdOutside, true)
}
function tdKeepAlive() { clearTimeout(TD.timer); TD.timer = setTimeout(tdDisarm, TD_ARM_MS) }
function tdTouchStart(e, k, onSwap) {
  if (e.touches.length !== 1) return
  const t = e.touches[0], now = performance.now()
  if (TD.armed !== k) {
    if (TD.last?.k === k && now - TD.last.t < TD_DOUBLE_MS) {
      TD.last = null
      tdDisarm()
      TD.armed = k; TD.armedEl = e.currentTarget
      TD.armedEl.classList.add('ws-tab-armed')
      document.addEventListener('touchstart', tdOutside, true)
    } else { TD.last = { k, t: now }; return }
  }
  tdKeepAlive()
  TD.onSwap = onSwap
  TD.drag = { x0: t.clientX, y0: t.clientY, moved: false, over: null }
}
// tapdrag2: the rail itself plus a strip TD_ZONE_PX below it count as that
// rail's drop zone; target = the tab nearest the finger's X.
const TD_ZONE_PX = 56, TD_GHOST_UP = 50
function tdZoneTab(x, y) {
  for (const rail of document.querySelectorAll('.ws-tab-rail')) {
    const r = rail.getBoundingClientRect()
    if (!r.width || x < r.left || x > r.right || y < r.top || y > r.bottom + TD_ZONE_PX) continue
    let best = null, bd = Infinity
    for (const tab of rail.querySelectorAll('[data-ws-tab]')) {
      const b = tab.getBoundingClientRect()
      const dx = x < b.left ? b.left - x : x > b.right ? x - b.right : 0
      if (dx < bd) { bd = dx; best = tab }
    }
    return best
  }
  return null
}
function tdMove(e) {
  const d = TD.drag; if (!d) return
  e.preventDefault()   // armed: the page must not scroll under the finger
  const t = e.touches[0]
  if (!d.moved && Math.hypot(t.clientX - d.x0, t.clientY - d.y0) < TD_SLOP) return
  d.moved = true
  tdKeepAlive()
  // tapdrag2: floating label ~50px ABOVE the finger so it stays visible.
  if (!d.ghost) {
    d.ghost = document.createElement('div')
    d.ghost.className = 'ws-tab-ghost'
    d.ghost.textContent = TD.armedEl?.textContent || ''
    document.body.appendChild(d.ghost)
  }
  d.ghost.style.transform = `translate(${t.clientX}px, ${t.clientY - TD_GHOST_UP}px) translate(-50%, -100%)`
  const hit = document.elementFromPoint(t.clientX, t.clientY)?.closest?.('[data-ws-tab]') || tdZoneTab(t.clientX, t.clientY)
  const over = hit && hit.dataset.wsTab !== TD.armed ? hit : null
  if (over !== d.over) { d.over?.classList.remove('ws-tab-drop'); over?.classList.add('ws-tab-drop'); d.over = over }
}
function tdEnd(e) {
  const d = TD.drag; if (!d) return
  if (!d.moved) { d.ghost?.remove(); TD.drag = null; return }   // the arming tap itself: stay armed
  if (e.cancelable) e.preventDefault()
  const from = TD.armed, to = d.over?.dataset.wsTab, swap = TD.onSwap
  tdDisarm()
  if (to) swap?.(from, to)
}
// touchmove/touchend target the touchstart element, so the rail sees the whole
// gesture; non-passive here (React's are passive) so preventDefault works.
function tdRailRef(el) {
  if (!el || el._td) return
  el._td = 1
  el.addEventListener('touchmove', tdMove, { passive: false })
  el.addEventListener('touchend', tdEnd, { passive: false })
  el.addEventListener('touchcancel', tdDisarm)
}

function SplitNode({ node, path, panels, onResize, onOpenMenu, onMaximize, onSetActive, onSwap, maximized, flipDirs, contentFlow, phonePx, onCellResize, armedKey, onArm }) {
  const splitRef = useRef(null)

  // Leaf — 1+ panels as tabs. ALL members stay mounted (inactive ones
  // display:none) so tape scroll / chat drafts / chart canvases survive
  // tab switches — same philosophy as maximize. The tab strip carries
  // .panel-rail so the right-click picker and double-click maximize work
  // on it for free.
  if (isLeaf(node)) {
    const list = node.panels ?? [node.panel]
    if (list.length === 0) return <PanelPlaceholder name="right-click to add a panel" />
    const active = list.includes(node.active) ? node.active : list[0]

    // ── Dual-chart cell ──
    // chart + chart2 in one cell occupy the SAME space — one visible at a time,
    // like tabs — but the switcher is the TWO SYMBOL DROPDOWNS in the chart's
    // header, not a tab strip. So: suppress the Workspace tab rail, keep both
    // mounted (display:none on the hidden one so canvases survive), and tell the
    // charts they're co-located. chart keeps its header and grows a 2nd dropdown
    // for chart2; chart2 is headerless. Clicking a dropdown sets which chart is
    // visible (node.active, via onSetActive) AND arms it (the chart's own
    // activate handlers). Detection is order-independent.
    const isDualChart =
      list.length === 2 && list.includes('chart') && list.includes('chart2')
    if (isDualChart) {
      // Both charts keep their REAL rail (all tools work). One visible at a
      // time; the visible chart's rail — which shows BOTH symbol dropdowns —
      // IS the header. cellSwitch lets the rail's sibling chip change which
      // body shows. No separate header bar, no headerless.
      const cellSwitch = (k) => onSetActive?.(path, k)
      return (
        <div className="ws-leaf ws-leaf-dualchart-overlay">
          {list.map(k => {
            const el = panels[k] ?? <PanelPlaceholder name={k} />
            const rendered = (el && el.type) ? cloneElement(el, { coLocated: true, cellSwitch }) : el
            return (
              <div
                key={k}
                className="ws-dualchart-overlay-pane"
                data-tour-id={k} data-tour-label={PANEL_LABELS[k] || k}
                style={{ display: k === active ? 'flex' : 'none' }}
              >
                {rendered}
              </div>
            )
          })}
        </div>
      )
    }

    // Every leaf carries a RAIL — a lone panel used to render neither a tab
    // strip (nothing to switch between) nor its own header (hidden by the
    // tabbed rule), so it had no identity bar at all (the "scan lost its rail"
    // report). A single-panel cell now shows one tab with its name.
    // EXCEPTION: a chart alone in a cell. The chart brings its OWN rail
    // (symbol chip, timeframe, chart-mode, density), so a workspace tab strip
    // above it would just say "CHART" and burn a row of height for nothing.
    // Any OTHER single panel, and every multi-panel cell (chart + something),
    // still gets the rail — you need the tabs to switch.
    const chartOnly = list.length === 1 && (list[0] === 'chart' || list[0] === 'chart2')
    const showRail = !chartOnly
    return (
      <div
        className={`ws-leaf${showRail ? ' ws-leaf-tabbed' : ''}${list.length > 1 ? ' ws-leaf-multi' : ''}`}
        data-active={active || undefined}
      >
        {showRail && (
          <div className="panel-rail ws-tab-rail" ref={tdRailRef}>
            {list.map(k => (
              <button
                key={k}
                className={`ws-tab${k === active ? ' active' : ''}`}
                onClick={() => onSetActive?.(path, k)}
                data-ws-tab={k}
                onTouchStart={(e) => tdTouchStart(e, k, onSwap)}
                draggable={!contentFlow}
                onDragStart={(e) => { e.dataTransfer.setData('text/x-ws-panel', k); e.dataTransfer.effectAllowed = 'move' }}
                onDragOver={(e) => { if (e.dataTransfer.types.includes('text/x-ws-panel')) { e.preventDefault(); e.currentTarget.classList.add('ws-tab-drop') } }}
                onDragLeave={(e) => e.currentTarget.classList.remove('ws-tab-drop')}
                onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('ws-tab-drop'); onSwap?.(e.dataTransfer.getData('text/x-ws-panel'), k) }}
              >{(PANEL_LABELS[k] || k)}</button>
            ))}
          </div>
        )}
        {list.map(k => (
          <div key={k} className="ws-tab-body" data-tour-id={k} data-tour-label={PANEL_LABELS[k] || k} style={{ display: k === active ? 'flex' : 'none' }}>
            {panels[k] ?? <PanelPlaceholder name={k} />}
          </div>
        ))}
      </div>
    )
  }

  // Portrait = the layout "rotates": EVERY split's axis flips (root row →
  // column, nested columns → rows), so a side rail becomes a top strip and
  // a side-by-side stack becomes a bottom row — not four flat strips.
  // Child ORDER is deliberately preserved (a strict geometric 90° would
  // reverse it on column→row): identical order means render paths ===
  // tree paths, so seam drags and the panel picker need no remapping.
  const baseDir = node.dir ?? 'row'
  const dir = flipDirs ? (baseDir === 'row' ? 'column' : 'row') : baseDir
  const isRow = dir === 'row'
  const total = node.sizes.reduce((a, b) => a + b, 0)

  return (
    <div className={`ws-split ${isRow ? 'ws-row' : 'ws-col'}`} ref={splitRef}>
      {node.children.map((child, i) => {
        const childPath = [...path, i]
        // Content-flow: the chart's resize seam must be IN-FLOW, in document
        // order, IMMEDIATELY after the chart cell — appending it after the
        // map (the first attempt) put it at the bottom of the column, where
        // it resized nothing anyone could see.
        const cellKey = childPath.join('.')
        const childIsLeaf = isLeaf(child)
        // Only LEAVES own a height. A cell that holds a nested split (what
        // "add cell" creates) sizes to ITS children instead — otherwise the new
        // cell had to share one 70vh box with its sibling, came out half-height
        // (no real initial size), and when it collapsed far enough there was
        // nothing left to right-click, which is why the panel picker seemed to
        // vanish on new cells. Leaves fix their box; splits just stack.
        const cellPx = contentFlow && childIsLeaf ? phonePx?.[cellKey] : null
        // Seam under every LEAF (the last one included). A cell holding a
        // nested split doesn't need one — the leaves inside it carry their own,
        // at whatever depth, via the recursive render.
        const flowSeamHere = contentFlow && childIsLeaf
        return (
          <Fragment key={i}>
          <div
            className="ws-cell"
            data-ws-cell={cellKey}
            style={
              maximized
                ? (maximized.length >= childPath.length &&
                   childPath.every((v, k) => maximized[k] === v)
                    // On the maximized path: this cell (and its split chain)
                    // expands to fill; siblings vanish below.
                    ? { flexGrow: 1, flexBasis: '100%', minWidth: 0, minHeight: 0 }
                    : { display: 'none' })
                : contentFlow
                  // TRUE-PHONE content-driven flow (matches the CSS breakpoint).
                  // The desktop values below (flexBasis:0 + minHeight:0) are
                  // INLINE, so they defeat every stylesheet release — they
                  // hard-lock each cell to a screen-share and the column can
                  // never exceed one screen (grown panels just overflowed onto
                  // the neighbour's rail). Here cells size to their content
                  // (basis:auto), push the cells below them, and the
                  // workspace-root scrolls the excess. flexGrow keeps filling
                  // the screen when the column is SHORT; minHeight is left to
                  // the phone CSS floor.
                  ? (childIsLeaf
                      ? {
                          flexGrow: 0,
                          flexBasis: 'auto',
                          height: cellPx != null ? cellPx : 'var(--phone-cell-h, 70vh)',
                          minWidth: 0,
                          minHeight: 0,
                          overflow: 'hidden',   // the PANEL scrolls inside; the box never moves
                        }
                      : {                        // holds a nested split → sizes to its children
                          flexGrow: 0,
                          flexBasis: 'auto',
                          height: 'auto',
                          minWidth: 0,
                          minHeight: 0,
                        })
                : { flexGrow: node.sizes[i] / total, flexBasis: 0, minWidth: 0, minHeight: 0 }
            }
            onContextMenu={(e) => {
              // Only a leaf cell owns a menu; nested splits bubble to their leaves.
              if (!isLeaf(child)) return
              // Panel-picker opens ONLY from a right-click on the panel's header
              // rail — so the chart body (and any panel body) keeps its own
              // right-click behavior untouched. Header rails share these classes.
              // EXCEPTION: an EMPTY cell (fresh split) has no rail at all —
              // its entire surface opens the picker, or it could never be
              // filled.
              const empty = child.panels != null && child.panels.length === 0
              const onHeader = e.target.closest(RAIL_SELECTOR)
              if (!empty && !onHeader) return
              e.preventDefault()
              e.stopPropagation()
              onOpenMenu(childPath, e.clientX, e.clientY)
            }}
            onDoubleClick={(e) => {
              // Double-click a panel's rail → maximize that cell over the
              // workspace; double-click again (or Esc) restores. Rail
              // SURFACE only — clicks on rail controls (buttons, inputs,
              // tabs) never hijack.
              if (!isLeaf(child)) return
              if (!e.target.closest(RAIL_SELECTOR)) return
              if (e.target.closest('button, input, select, a, [role="button"]')) return
              e.preventDefault()
              onMaximize?.(childPath)
            }}
          >
            <SplitNode node={child} path={childPath} panels={panels} onResize={onResize} onOpenMenu={onOpenMenu} onMaximize={onMaximize} onSetActive={onSetActive} onSwap={onSwap} maximized={maximized} flipDirs={flipDirs} contentFlow={contentFlow} phonePx={phonePx} onCellResize={onCellResize} armedKey={armedKey} onArm={onArm} />
          </div>
          {flowSeamHere && <FlowSeam cellKey={cellKey} cellPx={cellPx} onCellResize={onCellResize} armed={armedKey === cellKey} onArm={onArm} />}
          </Fragment>
        )
      })}
      {/* Seams overlay the boundaries; absolutely positioned so they take no
          layout space — cells stay butted tight. One per gap. */}
      {node.children.slice(0, -1).map((_, i) => {
        // Boundary position = cumulative weight fraction up to and incl. cell i.
        const frac = node.sizes.slice(0, i + 1).reduce((a, b) => a + b, 0) / total
        return (
          <Seam
            key={`seam-${i}`}
            isRow={isRow}
            frac={frac}
            splitRef={splitRef}
            path={path}
            index={i}
            onResize={onResize}
          />
        )
      })}
    </div>
  )
}

// Generic in-flow seam under any NON-chart cell on phone. Drag = set the height
// of the cell above it. Start height is MEASURED from the actual cell box, so
// the first drag continues from what you see.
function FlowSeam({ cellKey, cellPx, onCellResize, armed, onArm }) {
  // ONE MEANING: drag = set this cell's height. No pin/unpin, no citizenship
  // switch — in the fixed-box model every cell already has a height, so a seam
  // can only ever mean "change it".
  //   • double-tap  -> ARM. Unarmed, the seam is transparent to touch
  //     (touch-action: pan-y, no preventDefault) so swipes scroll straight
  //     through it instead of being caught as resizes.
  //   • armed -> the whole workspace becomes the drag surface (see the veil in
  //     Workspace). This strip still works as a drag handle, and each move
  //     refreshes the idle window.
  //   • drag while armed -> live resize, and you STAY armed. Exiting is a tap
  //     (on the veil), never an accident of letting go.
  //   • double-tap while armed -> reset this cell to the default height. This
  //     gesture stays on the strip, which is why the veil sits UNDER the seam
  //     in z-order rather than over it.
  // Taps are decided at pointer-UP with a 6px slop, so a drag can never be
  // mistaken for a tap.
  //
  // `armed` is a PROP now, owned by Workspace — see the adjust-mode block there
  // for why (one armed seam, one veil, no two-veil gesture fight).
  const stateRef = useRef({ drag: null, lastTapUp: 0 })
  return (
    <div
      className={`ws-seam ws-seam-h ws-seam-flow${armed ? ' armed' : ''}`}
      style={armed ? undefined : { touchAction: 'pan-y' }}
      onPointerDown={(e) => {
        const s = stateRef.current
        if (!armed) {
          s.drag = { y0: e.clientY, px0: 0, moved: false, arming: true }
        } else {
          e.preventDefault(); e.stopPropagation()
          const cell = e.currentTarget.previousSibling
          const startPx = cellPx ?? (cell?.getBoundingClientRect?.().height || 300)
          s.drag = { y0: e.clientY, px0: startPx, moved: false, arming: false }
          try { e.currentTarget.setPointerCapture(e.pointerId) } catch {}
        }
        const move = (ev) => {
          const d = s.drag
          if (!d) return
          const dy = ev.clientY - d.y0
          if (!d.moved && Math.abs(dy) > TAP_SLOP_PX) d.moved = true
          if (d.moved && !d.arming) { onCellResize(cellKey, d.px0 + dy); onArm(cellKey) }
        }
        const up = () => {
          const d = s.drag
          s.drag = null
          window.removeEventListener('pointermove', move)
          window.removeEventListener('pointerup', up)
          window.removeEventListener('pointercancel', up)
          // A drag is not a tap — and it no longer ends the mode. You stay
          // armed until you tap out. (This used to setArmed(false) here, which
          // is what forced you back to the strip for every single nudge.)
          if (!d || d.moved) return
          const now = Date.now()
          const isDouble = now - s.lastTapUp < 300
          s.lastTapUp = isDouble ? 0 : now
          if (!isDouble) return
          if (armed) onCellResize(cellKey, null)   // reset to default height…
          onArm(armed ? null : cellKey)            // …and out; or in, if we were out
        }
        window.addEventListener('pointermove', move)
        window.addEventListener('pointerup', up)
        window.addEventListener('pointercancel', up)
      }}
    />
  )
}

// Invisible drag strip centered on a boundary. Absolutely positioned at the
// cumulative-weight offset; zero layout footprint, faint highlight on hover.
function Seam({ isRow, frac, splitRef, path, index, onResize }) {
  const draggingRef = useRef(false)
  const startRef = useRef(0)
  const parentSizeRef = useRef(1)

  const onPointerDown = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    draggingRef.current = true
    startRef.current = isRow ? e.clientX : e.clientY
    const split = splitRef.current
    parentSizeRef.current = isRow ? (split?.clientWidth || 1) : (split?.clientHeight || 1)
    e.currentTarget.setPointerCapture(e.pointerId)
    document.body.style.cursor = isRow ? 'col-resize' : 'row-resize'
    document.body.style.userSelect = 'none'
    // Ghost chrome: while any seam is being dragged, reveal ALL seams so the
    // workspace structure is visible exactly when it's being manipulated.
    // Styled via `body.ws-dragging .ws-seam` in styles.css.
    document.body.classList.add('ws-dragging')
  }, [isRow, splitRef])

  const onPointerMove = useCallback((e) => {
    if (!draggingRef.current) return
    const pos = isRow ? e.clientX : e.clientY
    const deltaPx = pos - startRef.current
    startRef.current = pos
    onResize(path, index, deltaPx / parentSizeRef.current)
  }, [isRow, path, index, onResize])

  const endDrag = useCallback((e) => {
    if (!draggingRef.current) return
    draggingRef.current = false
    try { e.currentTarget.releasePointerCapture(e.pointerId) } catch {}
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
    document.body.classList.remove('ws-dragging')
  }, [])

  // Explicit hitbox thickness, centered on the boundary via negative margin
  // (not transform — CSS may already use transform on .ws-seam for the visible
  // hairline; we avoid colliding with it). This was the portrait-mode bug:
  // landscape (vertical seam) had a width in CSS but the horizontal seam had no
  // height, so stacked-layout boundaries were a zero-height, ungrabbable line.
  // GRAB = total hitbox thickness in px.
  const GRAB = 5
  const style = isRow
    ? { left: `${frac * 100}%`, top: 0, bottom: 0, width: GRAB, marginLeft: -GRAB / 2 }
    : { top: `${frac * 100}%`, left: 0, right: 0, height: GRAB, marginTop: -GRAB / 2 }

  return (
    <div
      className={`ws-seam ${isRow ? 'ws-seam-v' : 'ws-seam-h'}`}
      style={style}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    />
  )
}

// Right-click panel picker for a cell.
function CellMenu({ x, y, currentPanels = [], onToggle, onSplitH, onSplitV, onClose, onDismiss }) {
  const ref = useRef(null)
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onDismiss() }
    document.addEventListener('mousedown', handler)
    document.addEventListener('contextmenu', handler)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('contextmenu', handler)
    }
  }, [onDismiss])

  // Clamp to viewport.
  const W = 180, H = 360
  const left = Math.min(x, window.innerWidth - W - 8)
  const top = Math.min(y, window.innerHeight - H - 8)

  return (
    <div ref={ref} className="ws-menu" style={{ left, top }} onMouseDown={(e) => e.stopPropagation()}>
      <div className="ws-menu-label">Panels in this cell</div>
      {/* Checkbox semantics: checked = a tab in this cell. Click toggles;
          the menu stays open so several can be added/removed in one visit
          (click away to dismiss). Checking a panel that lives elsewhere
          MOVES it here. Unchecking the last panel is a no-op — use Close
          cell. */}
      {Object.entries(PANEL_LABELS)
        // Only offer panels allowed on the current viewport (PANEL_SCOPE):
        // Chart 2 is desktop-only, Trade is phone-only, the rest are 'both'.
        .filter(([key]) => panelInScope(key))
        .map(([key, label]) => (
        <button
          key={key}
          className="ws-menu-item"
          onClick={() => onToggle(key)}
        >
          <span className="ws-menu-check">{currentPanels.includes(key) ? '✓' : ''}</span>
          {label}
        </button>
      ))}
      <div className="ws-menu-divider" />
      <button className="ws-menu-item" onClick={onSplitH}>Split horizontal</button>
      <button className="ws-menu-item" onClick={onSplitV}>Split vertical</button>
      <div className="ws-menu-divider" />
      <button className="ws-menu-item ws-menu-danger" onClick={onClose}>Close cell</button>
    </div>
  )
}

function PanelPlaceholder({ name }) {
  return (
    <div className="ws-placeholder">
      <span className="ws-placeholder-label">{name}</span>
    </div>
  )
}